import { AxiosResponse } from 'axios';
import { useEffect, useState, useCallback } from 'react';
import { useLambdaFunctionUrl } from '../../../hooks/useEmailSettingLambda';
import API_ENDPOINTS from '../../../apiEndpoints';
import { validateEmailSend } from '../utils/validateEmailSend';
import { showErrorSnackbar, showWarningSnackbar } from '../../../components/snackBar/snakbar';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { axiosInstanceForLambda } from '../../../library/Axios';
import { Typography } from '@mui/system';
import { Error404SnackbarComponent } from '../utils/snackBarComponent';
import { set } from 'lodash';

export type EmailStatus = 'pending' | 'sent';

export interface Recipient {
  id: number;
  recipient: Boolean;
  recipientId: number;
  name?: string;
  email: string;
  invoiceNumber?: string;
  invoiceType?: string; // Invoice type code (e.g., 'PosInvoice', 'SalesInvoice')
  isActive?: number;
}

export interface SendEmailHookOptions {
  initialPageType?: string;
  recipients: Recipient[];
  onClose?: () => void;
  onProgress?: (progress: { sentEmails: number; totalEmails: number; message: string; progressPercentage: number }) => void;
  templateContent?: string;
  getTemplateContent?: () => string;
}

interface FinancialYear {
  startdate: number;
  enddate: number;
}

interface UserPreferenceAndReducer {
  financialYear?: FinancialYear;
}

interface RootState {
  userPrefrenceandReducer?: UserPreferenceAndReducer;
}

type EmailSendError = {
  status: number;
  message: string;
  response: AxiosResponse | any;
};

export type EmailSendResponse = {
  status: string; // "success" or "error"
  message: string;
  data: {
    summary: {
      total: number;
      success: number;
      failed: number;
    };
    details: {
      email: string;
      party: string;
      status: string; // e.g., "Success" or "Failed"
      message: string;
      vouchernumber?: string;
      recipient?: string;
    }[];
  };
} | null;

export type EmailSendData = NonNullable<EmailSendResponse>['data'];
export function useSendEmail({ initialPageType = '', recipients, onClose, onProgress, templateContent, getTemplateContent }: SendEmailHookOptions = { recipients: [] }) {
  const [loading, setLoading] = useState(false);
  const [pageType, setPageType] = useState(initialPageType);
  const [statuses, setStatuses] = useState<Record<string, EmailStatus>>({});
  const [modalHeader, setModalHeader] = useState('');
  const [dateRange, setDateRange] = useState<{ from: number | null; to: number | null }>({ from: null, to: null });
  const [responseSummary, setResponseSummary] = useState<{ open: boolean; data: EmailSendResponse }>({ open: false, data: null });
  const { callLambdaFunctionUrl } = useLambdaFunctionUrl();

  const { startdate, enddate } = useSelector((state: RootState) => state.userPrefrenceandReducer?.financialYear || {}) as FinancialYear;

  useEffect(() => {
    setPageType(initialPageType);
  }, [initialPageType]);

  useEffect(() => {
    switch (pageType) {
      case 'customer':
        setModalHeader('Send Transactional Email to Customers');
        break;
      case 'supplier':
        setModalHeader('Send Transactional Email to Suppliers');
        break;
      case 'salesInvoice':
        setModalHeader('Send Invoice Email');
        break;
      case 'purchaseInvoice':
        setModalHeader('Send Purchase Invoice Email');
        break;
      case 'receipt':
        setModalHeader('Send Receipt Email');
        break;
      case 'payment':
        setModalHeader('Send Payment Confirmation Email');
        break;
      case 'creditNote':
        setModalHeader('Send Credit Note Email');
        break;
      case 'debitNote':
        setModalHeader('Send Debit Note Email');
        break;
      case 'pos':
        setModalHeader('Send POS Invoice Email');
        break;
      default:
        setModalHeader('');
    }
  }, [pageType]);

  const updateStatus = useCallback((id: string, status: EmailStatus) => {
    setStatuses((prev) => ({ ...prev, [id]: status }));
  }, []);

  const handleDateRangeChange = useCallback((newRange: { from: number | null; to: number | null }) => {
    setDateRange(newRange);
  }, []);

  const handleSendEmail = useCallback(async () => {
    setLoading(true);
    
    // Reset progress state
    if (onProgress) {
      onProgress({
        sentEmails: 0,
        totalEmails: 0,
        message: 'Preparing to send emails...',
        progressPercentage: 0,
      });
    }

    // Declare animationCancelled outside try block so it's accessible in catch block
    let animationCancelled = false;

    try {
      const activeRecipients = recipients.filter((r) => r.isActive === 1);
      const { valid, error } = validateEmailSend(activeRecipients, pageType, dateRange);

      if (!valid) {
        showErrorSnackbar(error || '', 'right', 'bottom');
        setLoading(false);
        if (onProgress) {
          onProgress({
            sentEmails: 0,
            totalEmails: 0,
            message: 'Validation failed',
            progressPercentage: 0,
          });
        }
        return;
      }

      // Smooth progress animation function
      let currentProgress = 0;
      
      const animateProgress = (targetProgress: number, message: string, duration: number = 1000) => {
        return new Promise<void>((resolve, reject) => {
          if (!onProgress) {
            resolve();
            return;
          }

          if (animationCancelled) {
            reject(new Error('Animation cancelled'));
            return;
          }

          const startTime = Date.now();
          const startProgress = currentProgress;
          
          const updateProgress = () => {
            if (animationCancelled) {
              reject(new Error('Animation cancelled'));
              return;
            }

            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function for smooth animation (ease-out)
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            const newProgress = startProgress + (targetProgress - startProgress) * easedProgress;
            currentProgress = newProgress;
            
            onProgress({
              sentEmails: Math.floor((currentProgress / 100) * activeRecipients.length),
              totalEmails: activeRecipients.length,
              message,
              progressPercentage: currentProgress,
            });
            
            if (progress < 1) {
              requestAnimationFrame(updateProgress);
            } else {
              currentProgress = targetProgress;
              resolve();
            }
          };
          
          requestAnimationFrame(updateProgress);
        });
      };

      // Stage 1: Initialization (0% to 15%)
      await animateProgress(15, 'Preparing to send emails...', 800);

      // Check if we have POS invoices and adjust the type accordingly
      const hasPOSInvoices = activeRecipients.some((r) => r.invoiceType === 'PosInvoice');
      
      // Type mapping: Only 'payment' should map to 'paymentconfirm'
      // salesInvoice -> 'invoice', purchaseInvoice -> 'purchase', etc.
      const typeMapping: Record<string, string> = {
        customer: 'transaction',
        supplier: 'transaction',
        salesinvoice: 'invoice',
        purchaseinvoice: 'purchase',
        receipt: 'receipt',
        payment: 'paymentconfirm', 
        creditnote: 'creditnote',
        debitnote: 'debitnote',
      };
      
      const type = hasPOSInvoices 
        ? 'pos'  // Use 'pos' type for POS invoices
        : typeMapping[initialPageType]; // Use explicit mapping, no fallback


      const ids: (string | number)[] = activeRecipients.map((r) => r.id ?? '');

      if (ids.length === 0) {
        showWarningSnackbar('No valid recipient IDs available');
        setLoading(false);
        if (onProgress) {
          onProgress({
            sentEmails: 0,
            totalEmails: 0,
            message: 'No recipients available',
            progressPercentage: 0,
          });
        }
        return;
      }

      // Stage 2: Connecting to service (15% to 30%)
      await animateProgress(30, 'Connecting to email service...', 600);

      // Get the latest template content (either from prop or function)
      const latestTemplateContent = getTemplateContent ? getTemplateContent() : templateContent;
      
      // For POS invoices, we need to include email addresses in the payload
      const posRecipients = activeRecipients.filter((r) => r.invoiceType === 'PosInvoice');
      const regularRecipients = activeRecipients.filter((r) => r.invoiceType !== 'PosInvoice');
      
      // Build base payload with emailBody for all types (invoice, purchase, receipt, payment, etc.)
      const basePayload = {
        ...buildEmailPayload(type, ids, {
          startDate: dateRange.from || undefined,
          endDate: dateRange.to || undefined,
        }),
        // Always include emailBody for all email types (invoice, purchase, receipt, payment, etc.)
        ...(latestTemplateContent ? { emailBody: latestTemplateContent } : {})
      };
      
      let payload;
      if (posRecipients.length > 0) {
        // For POS invoices, include email addresses in the payload
        payload = {
          ...basePayload,
          posEmails: posRecipients.map((r) => ({
            invoiceid: r.id,
            email: r.email
          })),
        };
      } else {
        // For regular invoices/receipts/payments/etc., use the standard payload
        payload = basePayload;
      }

      // Stage 3: Processing recipients (30% to 45%)
      await animateProgress(45, 'Processing recipients...', 700);

      // Stage 4: Preparing API call (45% to 60%)
      await animateProgress(60, 'Preparing email data...', 500);

      // Start the actual API call
      const apiCallPromise = axiosInstanceForLambda({
        url: API_ENDPOINTS.EMAIL_SENDING_URL,
        data: payload,
        method: 'POST',
      });

      // Stage 5: API call in progress (60% to 85%) - animate while waiting
      const apiProgressPromise = animateProgress(85, 'Sending emails...', 2000);

      // Wait for API call, but handle errors properly
      let response;
      try {
        // Race between API call and progress animation
        const [apiResponse] = await Promise.all([
          apiCallPromise.catch((err) => {
            // Cancel animation if API fails
            animationCancelled = true;
            throw err;
          }),
          apiProgressPromise.catch(() => {
            // Ignore animation cancellation errors
          }),
        ]);
        response = apiResponse;
      } catch (error) {
        // Cancel animation and reset progress
        animationCancelled = true;
        throw error;
      }

      // Check if response indicates an error
      const data = response?.data as EmailSendResponse;
      
      if (!data) {
        throw new Error('No response data received from server');
      }

      // Check if the response status indicates an error
      if (data.status === 'error') {
        // If data is null but we have an error message, create a structured error response for the modal
        if (!data.data && data.message) {
          const errorResponse: EmailSendResponse = {
            status: 'error',
            message: data.message,
            data: {
              summary: {
                total: 1,
                success: 0,
                failed: 1,
              },
              details: [
                {
                  email: '',
                  party: 'Error',
                  status: 'Failed',
                  message: data.message,
                },
              ],
            },
          };
          // Stage 6: Completion (85% to 100%)
          await animateProgress(100, 'Email sending completed', 300);
          setResponseSummary({ open: true, data: errorResponse });
          setLoading(false);
          return;
        } else {
          // For other error cases, throw error
          throw new Error(data.message || 'Email sending failed');
        }
      }

      // Stage 6: Completion (85% to 100%)
      await animateProgress(100, 'Emails sent successfully!', 800);
      
      setResponseSummary({ open: true, data: data || null });
      setLoading(false);
      
    } catch (error: any) {
      // Cancel any ongoing animations
      animationCancelled = true;
      
      // Reset progress state
      if (onProgress) {
        onProgress({
          sentEmails: 0,
          totalEmails: 0,
          message: 'Error occurred',
          progressPercentage: 0,
        });
      }

      // Handle different types of errors
      let errorMessage = 'Failed to send email';
      
      if (error?.response) {
        // Server responded with error status
        const statusCode = error.response?.status || 500;
        const serverMessage = error.response?.data?.message || error.response?.data?.error;
        const responseData = error.response?.data;
        
        // Check if this is a business logic error that should be shown in the modal
        // (e.g., "No payment confirmations found to send emails")
        // Handle cases where response has status: "error", 404, or 400 with error message
        if (serverMessage && (responseData?.status === 'error' || statusCode === 404 || statusCode === 400)) {
          const errorResponse: EmailSendResponse = {
            status: 'error',
            message: serverMessage,
            data: {
              summary: {
                total: 1,
                success: 0,
                failed: 1,
              },
              details: [
                {
                  email: '',
                  party: 'Error',
                  status: 'Failed',
                  message: serverMessage,
                },
              ],
            },
          };
          setResponseSummary({ open: true, data: errorResponse });
          setLoading(false);
          return;
        }
        
        // For other errors, show snackbar
        if (statusCode === 404) {
          Error404SnackbarComponent({ message: serverMessage || 'Resource not found' });
        } else if (statusCode === 400) {
          showErrorSnackbar(serverMessage || 'Invalid request. Please check your input.', 'right', 'bottom');
        } else if (statusCode === 500) {
          showErrorSnackbar(serverMessage || 'Server error occurred. Please try again later.', 'right', 'bottom');
        } else {
          showErrorSnackbar(serverMessage || `Server error (${statusCode})`, 'right', 'bottom');
        }
      } else if (error?.request) {
        // Request was made but no response received (network error, timeout, etc.)
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          showErrorSnackbar('Request timeout. Please check your connection and try again.', 'right', 'bottom');
        } else if (error.message?.includes('Network Error')) {
          showErrorSnackbar('Network error. Please check your internet connection.', 'right', 'bottom');
        } else {
          showErrorSnackbar('Unable to connect to server. Please check your connection.', 'right', 'bottom');
        }
      } else if (error?.message) {
        // Other errors (validation, etc.)
        showErrorSnackbar(error.message, 'right', 'bottom');
      } else {
        // Unknown error
        showErrorSnackbar(errorMessage, 'right', 'bottom');
      }

      // Always reset loading state on error
      setLoading(false);
      
      // Log error for debugging
      console.error('Email sending error:', error);
    }
  }, [initialPageType, recipients, dateRange, callLambdaFunctionUrl, onProgress, templateContent, getTemplateContent, pageType]);

  const buildEmailPayload = (
    type: string,
    ids: Array<string | number>,
    params?: {
      startDate?: number;
      endDate?: number;
    }
  ): {
    ids: Array<string | number>;
    startDate?: number;
    endDate?: number;
    type: string;
    emailBody?: string;
  } => {
    return type === 'transaction'
      ? {
          ids,
          startDate: params?.startDate || undefined,
          endDate: params?.endDate || undefined,
          type,
        }
      : {
          ids,
          type,
        };
  };

  const resetAllStates = useCallback(() => {
    setLoading(false);
    setPageType(initialPageType);
    setStatuses({});
    setDateRange({ from: null, to: null });
    // Reset progress state
    if (onProgress) {
      onProgress({
        sentEmails: 0,
        totalEmails: 0,
        message: '',
        progressPercentage: 0,
      });
    }
    onClose?.();
  }, [initialPageType, onClose, onProgress]);

  const handleCloseWrapperModal = useCallback(() => {
    setResponseSummary({ open: false, data: null });
    resetAllStates();
  }, [resetAllStates]);

  const handleCloseResponseSummary = useCallback(() => {
    setResponseSummary((prev) => ({ ...prev, open: false, data: null }));
    resetAllStates();
  }, [resetAllStates]);

  return {
    loading,
    setLoading,
    pageType,
    setPageType,
    statuses,
    updateStatus,
    dateRange,
    handleDateRangeChange,
    modalHeader,
    handleSendEmail,
    responseSummary,
    handleCloseResponseSummary,
    startdate,
    enddate,
    handleCloseWrapperModal,
  };
}
